// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { Store } from 'antd/lib/form/interface';
import { slugify, useLocalMessage } from '@Helpers/';
import { FormInstance } from 'antd/lib/form';
import { CustomFieldOutput, CustomFieldType, CustomFieldTypeValues } from 'thehive-sdk';
import { THForm } from '@Molecules/THForm/THForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THTextArea } from '@Molecules/Inputs/THTextArea/THTextArea';
import { THAutoComplete } from '@Molecules/Inputs/THAutoComplete/THAutoComplete';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';

import './CustomFieldForm.scss';

export interface CustomFieldValuesForm {
    name: string;
    displayName?: string;
    type: CustomFieldType;
    options?: string;
    description: string;
    mandatory?: boolean;
    group?: string;
}

interface CustomFieldFormProps {
    onFinish(values: CustomFieldValuesForm): void;
    form: FormInstance;
    defaultValues?: CustomFieldOutput;
    isValid: boolean;
    onChange: () => void;
    customFieldList?: CustomFieldOutput[];
    onCancel: () => void;
}

export const CustomFieldForm = ({
    onFinish,
    form,
    isValid,
    defaultValues,
    onChange,
    customFieldList,
    onCancel,
}: CustomFieldFormProps): ReactElement => {
    const i = useLocalMessage();

    const { nameList, groupList } = customFieldList?.reduce(
        (acc, { group, name }) => {
            return {
                ...acc,
                groupList: group && !acc.groupList.includes(group) ? [...acc.groupList, group] : acc.groupList,
                nameList: name ? [...(acc.nameList || []), name] : acc.nameList,
            };
        },
        { groupList: [], nameList: [] } as {
            groupList: string[];
            nameList: string[];
        },
    ) || { groupList: [], nameList: [] };

    const onValuesChange = (changedValue: Store): void => {
        if ('displayName' in changedValue && !defaultValues) {
            form.setFieldsValue({ name: slugify(changedValue['displayName']) });
        }

        if ('type' in changedValue) {
            form.setFieldsValue({ options: undefined });
        }

        onChange();
    };

    const tradLabelKey = !!defaultValues ? 'edit' : 'create';

    return (
        <THForm<CustomFieldValuesForm>
            name="custom-field-form"
            className="custom-field-form"
            onFinish={onFinish}
            form={form}
            onValuesChange={onValuesChange}
            initialValues={{ group: 'default', ...defaultValues, options: defaultValues?.options.join('\n') || '' }}
        >
            <THForm.Layout
                footer={
                    <FormSubmitButton
                        onCancel={onCancel}
                        footerText={i(`customField.${tradLabelKey}.form.submit`)}
                        disabled={!isValid}
                    />
                }
            >
                <THForm.Item
                    label={i('customField.create.form.display-name.label')}
                    name="displayName"
                    rules={[{ required: true, message: i('customField.create.form.name.required') }]}
                >
                    <THInput placeholder={i('customField.create.form.name.placeholder')} />
                </THForm.Item>

                <THForm.Item
                    label={i('customField.create.form.technical-name.label')}
                    name="name"
                    tooltip={i('customField.create.form.technical-name.help')}
                    rules={[
                        { required: true, message: i('customField.create.form.name.required') },
                        {
                            message: i('common.already-exist'),
                            validator: (_, value) => {
                                if (defaultValues?.name !== value && nameList.includes(value)) {
                                    return Promise.reject();
                                } else {
                                    return Promise.resolve();
                                }
                            },
                        },
                    ]}
                >
                    <THInput
                        placeholder={i('customField.create.form.technical-name.placeholder')}
                        disabled={!!defaultValues}
                    />
                </THForm.Item>

                <THForm.Item
                    name="description"
                    label={i('customField.create.form.description.label')}
                    rules={[{ required: true, message: i('customField.create.form.description.required') }]}
                >
                    <THTextArea placeholder={i('customField.create.form.description.placeholder')} />
                </THForm.Item>

                <THForm.Item name="group" label={i('task.create.form.group.label')}>
                    <THAutoComplete
                        options={groupList?.map((group) => ({ label: group, value: group }))}
                        placeholder={i('task.create.form.group.placeholder')}
                    />
                </THForm.Item>

                <THForm.Item
                    label={i('customField.create.form.type.label')}
                    name="type"
                    rules={[{ required: true, message: i('customField.create.form.type.required') }]}
                >
                    <THSelect
                        options={CustomFieldTypeValues.map((value) => ({ label: value, value }))}
                        placeholder={i('customField.create.form.type.placeholder')}
                    />
                </THForm.Item>

                <THForm.Item<CustomFieldValuesForm> noStyle shouldUpdate={(prev, curr) => prev.type !== curr.type}>
                    {({ getFieldValue }) => (
                        <THForm.Item
                            name="options"
                            label={i('customField.create.form.options.label')}
                            hidden={!['string', 'integer', 'float'].includes(getFieldValue('type'))}
                        >
                            <THTextArea
                                placeholder={i('customField.create.form.options.placeholder')}
                                className="custom-field-form__text-area"
                            />
                        </THForm.Item>
                    )}
                </THForm.Item>

                <THForm.Item
                    name="mandatory"
                    label={i('customField.create.form.mandatory.label')}
                    valuePropName="checked"
                    tooltip={i('customField.create.form.mandatory.help')}
                >
                    <THSwitch />
                </THForm.Item>
            </THForm.Layout>
        </THForm>
    );
};
